import { useCallback, useMemo } from 'react';
import { defineMessage } from 'react-intl';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { FilterAccordion } from '@sorare/core/src/atoms/layout/accordion/FilterAccordion';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useLocalStorage } from '@sorare/core/src/hooks/useLocalStorage';
import { useQueryState } from '@sorare/core/src/hooks/useQueryState';
import { getFormattedSport } from '@sorare/core/src/lib/sports';
import { SportSelectType } from '@sorare/core/src/lib/storage/localStorage';

const SportSelectValue = {
  [SportSelectType.ALL_SPORTS]: [Sport.FOOTBALL, Sport.BASEBALL, Sport.NBA],
  [SportSelectType.FOOTBALL]: [Sport.FOOTBALL],
  [SportSelectType.BASEBALL]: [Sport.BASEBALL],
  [SportSelectType.NBA]: [Sport.NBA],
};

const messages = {
  [SportSelectType.ALL_SPORTS]: defineMessage({
    defaultMessage: 'All Sports',
  }),
  [SportSelectType.FOOTBALL]: getFormattedSport(Sport.FOOTBALL),
  [SportSelectType.BASEBALL]: getFormattedSport(Sport.BASEBALL),
  [SportSelectType.NBA]: getFormattedSport(Sport.NBA),
};

export const useSportSelect = () => {
  const { formatMessage } = useIntlContext();

  const [selectedSport, setSelectedSport] = useLocalStorage(
    'MySorare.selectedSport',
    SportSelectType.ALL_SPORTS
  );

  const [{ sportType }, setSortType] = useQueryState({
    sportType: selectedSport || SportSelectType.ALL_SPORTS,
  });

  const onSportChange = useCallback(
    (value?: SportSelectType) => {
      if (!value) return;
      setSortType({ sportType: value });
      setSelectedSport(value);
    },
    [setSelectedSport, setSortType]
  );

  const sportOptions = useMemo(
    () =>
      Object.values(SportSelectType).map(element => ({
        label:
          typeof messages[element] === 'string'
            ? messages[element]
            : formatMessage(messages[element]),
        value: element,
      })),
    [formatMessage]
  );

  return {
    sportOptions,
    sportType,
    sportSelectNode: (
      <FilterAccordion startsOpen title={<LabelM bold>Sport</LabelM>}>
        <RadioGroup
          name="sport-selector"
          value={sportType}
          options={sportOptions}
          onChange={onSportChange}
        />
      </FilterAccordion>
    ),
    sportInputValue: SportSelectValue[sportType],
  };
};
